import { useEffect, useRef, useState } from "react";
import { Sparkles, Send, X, Trash2, Bot, User as UserIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { API_BASE } from "@/lib/api";

const EXAMPLE_PROMPTS = [
  "Welke 3 klanten hebben dit jaar de hoogste marge?",
  "Hoe doet juni 2026 vs juni 2025 op omzet?",
  "Welk product heeft de slechtste marge?",
  "Vat mijn financiële prestaties samen.",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/chat/messages")
      .then(({ data }) => setMessages(data.messages || []))
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const resp = await fetch(`${API_BASE}/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
        body: JSON.stringify({ message: msg }),
        signal: controller.signal,
      });
      if (!resp.ok || !resp.body) {
        throw new Error(`HTTP ${resp.status}`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (!payload) continue;
          try {
            const ev = JSON.parse(payload);
            if (ev.delta) {
              setMessages((prev) => {
                const cp = [...prev];
                cp[cp.length - 1] = { role: "assistant", content: cp[cp.length - 1].content + ev.delta };
                return cp;
              });
            } else if (ev.error) {
              toast.error(ev.error);
            }
          } catch (parseErr) {
            console.warn("SSE parse error (skipping frame):", parseErr?.message || parseErr);
          }
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        toast.error(`Chatfout: ${e.message}`);
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const clear = async () => {
    if (!window.confirm("Volledige chatgeschiedenis wissen?")) return;
    await api.delete("/chat/messages");
    setMessages([]);
    toast.success("Chat gewist");
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-open-btn"
          className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-[60] h-14 w-14 rounded-full bg-slate-900 text-white shadow-[0_8px_24px_-6px_rgba(15,23,42,0.4)] hover:bg-slate-800 hover:scale-105 transition-all flex items-center justify-center group"
          aria-label="AI assistent openen"
        >
          <Sparkles className="h-6 w-6 group-hover:rotate-12 transition-transform" />
          <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-sky-400 ring-2 ring-white animate-pulse" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed inset-x-3 bottom-3 top-3 sm:top-auto sm:bottom-6 sm:right-6 sm:left-auto sm:inset-x-auto sm:w-[420px] sm:h-[min(640px,calc(100vh-4rem))] z-[60] bg-white rounded-xl shadow-[0_24px_64px_-12px_rgba(15,23,42,0.3)] border border-slate-200 flex flex-col overflow-hidden"
        >
          <div className="px-5 py-4 bg-slate-900 text-white flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-md bg-sky-500/20 border border-sky-500/30 flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-sky-300" />
              </div>
              <div>
                <p className="text-sm font-semibold">Lifjalla AI</p>
                <p className="text-[10px] text-slate-400 tracking-wider uppercase">Claude Sonnet 4.5</p>
              </div>
            </div>
            <div className="flex gap-1">
              <button
                onClick={clear}
                title="Geschiedenis wissen"
                data-testid="chat-clear-btn"
                className="h-8 w-8 rounded-md hover:bg-slate-800 flex items-center justify-center text-slate-300 hover:text-white transition-colors"
              >
                <Trash2 className="h-4 w-4" />
              </button>
              <button
                onClick={() => setOpen(false)}
                data-testid="chat-close-btn"
                className="h-8 w-8 rounded-md hover:bg-slate-800 flex items-center justify-center text-slate-300 hover:text-white transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4 bg-slate-50" data-testid="chat-messages">
            {messages.length === 0 && !streaming && (
              <div className="space-y-3">
                <div className="text-center py-6">
                  <div className="inline-flex h-12 w-12 rounded-full bg-sky-50 border border-sky-100 items-center justify-center mb-3">
                    <Bot className="h-6 w-6 text-sky-700" />
                  </div>
                  <p className="text-sm font-semibold text-slate-900">Stel een vraag over je data</p>
                  <p className="text-xs text-slate-500 mt-1">Ik ken al je omzet, marges, klanten en regio&apos;s.</p>
                </div>
                <div className="space-y-2">
                  {EXAMPLE_PROMPTS.map((p) => (
                    <button
                      key={p}
                      onClick={() => send(p)}
                      data-testid={`chat-example-${p.substring(0, 15)}`}
                      className="w-full text-left text-xs px-3 py-2 rounded-md bg-white border border-slate-200 hover:border-sky-300 hover:bg-sky-50/50 transition-colors text-slate-700"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={m.id || `msg-${i}-${m.role}`} className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`h-7 w-7 shrink-0 rounded-md flex items-center justify-center ${m.role === "user" ? "bg-slate-900 text-white" : "bg-sky-50 text-sky-700 border border-sky-100"}`}>
                  {m.role === "user" ? <UserIcon className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}
                </div>
                <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${m.role === "user" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                  <div className="whitespace-pre-wrap leading-relaxed">{m.content || (streaming && i === messages.length - 1 ? <span className="inline-flex gap-1"><span className="h-1.5 w-1.5 rounded-full bg-sky-400 animate-pulse" /><span className="h-1.5 w-1.5 rounded-full bg-sky-400 animate-pulse" style={{ animationDelay: "150ms" }} /><span className="h-1.5 w-1.5 rounded-full bg-sky-400 animate-pulse" style={{ animationDelay: "300ms" }} /></span> : "")}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-slate-200 p-3 bg-white">
            <div className="flex gap-2 items-end">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Stel een vraag over je data…"
                rows={1}
                className="resize-none text-sm min-h-[40px] max-h-32"
                data-testid="chat-input"
                disabled={streaming}
              />
              <Button
                onClick={() => send()}
                disabled={streaming || !input.trim()}
                size="icon"
                className="bg-slate-900 hover:bg-slate-800 shrink-0 h-10 w-10"
                data-testid="chat-send-btn"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
